import { Link } from "react-router-dom";
import { Article, ArticleWithCategory } from "@/hooks/useArticles";
import { ArrowRight, Clock } from "lucide-react";

// Import local images (WebP for better performance)
import followDream from "@/assets/images/follow-dream.webp";
import roadtripVan from "@/assets/images/roadtrip-van.webp";
import airplaneSunset from "@/assets/images/airplane-sunset.jpg";
import italyCoast from "@/assets/images/italy-coast.webp";
import airportSilhouette from "@/assets/images/airport-silhouette.webp";
import lakeBoat from "@/assets/images/lake-boat.webp";
import friendsMountain from "@/assets/images/friends-mountain.webp";

interface ArticleCardProps {
  article: Article | ArticleWithCategory;
  colorAccent?: string;
  featured?: boolean;
}

export function ArticleCard({ article, colorAccent = "coral", featured = false }: ArticleCardProps) {
  const colorMap: Record<string, string> = {
    coral: "bg-accent text-accent-foreground",
    marine: "bg-primary text-primary-foreground",
    sage: "bg-ms-sage text-white",
    sky: "bg-ms-sky text-white",
    lavender: "bg-ms-lavender text-white",
  };

  const badgeClass = colorMap[colorAccent] || colorMap.coral;

  // Estimate read time (roughly 200 words per minute)
  const wordCount = article.content?.split(/\s+/).length || 0;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  // Format date
  const formattedDate = new Date(article.created_at).toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  // All unique local images for fallback
  const placeholderImages = [
    followDream,
    roadtripVan,
    airplaneSunset,
    italyCoast,
    airportSilhouette,
    lakeBoat,
    friendsMountain,
  ];
  
  // Use a hash of article id to consistently pick a unique placeholder
  const getImageIndex = (id: string): number => {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = ((hash << 5) - hash) + id.charCodeAt(i);
      hash = hash & hash;
    }
    return Math.abs(hash) % placeholderImages.length;
  };
  
  // Use featured_image if available, otherwise fallback to placeholder
  const imageUrl = article.featured_image || placeholderImages[getImageIndex(article.id)];

  if (featured) {
    return (
      <Link
        to={`/artikel/${article.slug}`}
        className="group block bg-card rounded-2xl overflow-hidden border border-border transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
      >
        <div className="aspect-[16/10] overflow-hidden">
          <img 
            src={imageUrl}
            alt={`Billede til artikel: ${article.title}`}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        
        <div className="p-6">
          <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-3 ${badgeClass}`}>
            {'article_type' in article && article.article_type?.name 
              ? article.article_type.name 
              : ('category' in article && article.category?.name ? article.category.name : 'Sabbatår')}
          </span>
          
          <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2">
            {article.title}
          </h3>
          
          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
            {article.excerpt}
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>{formattedDate}</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {readTime} min
              </span>
            </div>
            
            <span className="inline-flex items-center text-sm font-semibold text-primary group-hover:gap-2 transition-all gap-1">
              Læs mere
              <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/artikel/${article.slug}`}
      className="group block bg-card rounded-2xl overflow-hidden border border-border transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img 
          src={imageUrl}
          alt={`Billede til artikel: ${article.title}`}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      
      <div className="p-5">
        <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full mb-3 ${badgeClass}`}>
          {'article_type' in article && article.article_type?.name 
            ? article.article_type.name 
            : ('category' in article && article.category?.name ? article.category.name : 'Sabbatår')}
        </span>
        
        <h3 className="text-base font-bold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
          {article.title}
        </h3>
        
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {article.excerpt}
        </p>
        
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>{formattedDate}</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {readTime} min
          </span>
        </div>
      </div>
    </Link>
  );
}
